/* eslint-disable */
import {getPageData, getPages, getGlobals, getLayoutData} from '../api/api';
import styled from 'styled-components';
import {ProjectBanner, NextProject, useExtendedTheme, useScrollContext, StickyHeading} from "@repo/ui";
import {CONTENT_TYPES as COMMON_CONTENT_TYPES} from '@repo/utils/constants';
import {CONTENT_TYPES as DESIGN_COMMON_TYPES} from '@/utils/constants';
import {useRouter} from "next/router";
import {useCallback, useMemo, useRef} from "react";
import SingleProjectElevatorNav from "@/components/singleProjectElevatorNav/SingleProjectElevatorNav";
import ExtendedContent from "@/components/common/ExtendedContent";
import {duration} from "@repo/utils/gsap";
import {capitalize, setCssVars} from "@repo/utils";

const StyledWrapper = styled.main`
	color: ${({ theme }) => theme.palette.primary.text};
	background-color: ${({ theme }) => theme.palette.primary.background};
	display: flex;
	flex-direction: column;
	height: 100vh;
	padding: 70px var(--paddingX);
	row-gap: 50px;
	width: 100%;
	transition: color, background-color 0.3s var(--ease);

	h1 {
		font-size: 6rem;
	}

	a {
		font-size: 2rem;
	}

	@media (max-width: 767px) {
		padding: 70px calc(var(--paddingX) * 0.5);
		row-gap: 15px;
		h1 {
			font-size: 3rem;
		}

		a {
			font-size: 1rem;
		}
	}
`;

const pathsQuery = `
query GetSingleProject($locale: SiteLocale) {
	allDesignSingleProjects(locale: $locale, first: 100) {
		name
	  	slug
	}
}
`;

const propsQuery = `
	query GetProject($slug: String, $locale: SiteLocale) {
		allDesignSingleProjects(locale: $locale, filter: { slug: { eq: $slug } }) {
			name
			slug
			highlight
			_seoMetaTags {
				attributes
				content
				tag
			}
			projectColor {
				hex
			}
			projectServiceRange {
				serviceName
			}
			projectCategories {
				projectCategory
				projectCategoryId
			}
			description
			bannerWhiteLogo
			bannerWhiteMenu
			year
			themeLight {
				mainPrimary {
					hex
				}
				mainSecondary {
					hex
				}
				mainTertiary {
					hex
				}
				extraPrimary {
					hex
				}
				extraSecondary {
					hex
				}
				extraTertiary {
					hex
				}
			}
			themeDark {
				mainPrimary {
					hex
				}
				mainSecondary {
					hex
				}
				mainTertiary {
					hex
				}
				extraPrimary {
					hex
				}
				extraSecondary {
					hex
				}
				extraTertiary {
					hex
				}
			}
			coverPortrait {
				url
			}
			coverLandscape {
				url
			}
			breadcrumbs {
				text
				id
				link {
					... on ${DESIGN_COMMON_TYPES.ALL_PROJECTS_PAGE} {
						slug
					}
					... on ${DESIGN_COMMON_TYPES.SINGLE_PAGE} {
						slug
					}
					... on ${DESIGN_COMMON_TYPES.SINGLE_PROJECT} {
						slug
					}
				}
			}
			projectNavigationElevator {
				link {
					__typename
					... on ${DESIGN_COMMON_TYPES.PROJECT_RANGE} { 
					id
					}
					... on ${DESIGN_COMMON_TYPES.ASSET_WITH_HEADER} { 
					id
					}
					... on ${DESIGN_COMMON_TYPES.HEADER_AND_DESCRIPTION} { 
					id
					}
					... on ${DESIGN_COMMON_TYPES.PINNED_TEXT_WITH_IMAGE} { 
					id
					}
					... on ${COMMON_CONTENT_TYPES.PROJECT_SLIDER} {
					id
					}
					... on ${DESIGN_COMMON_TYPES.TECHNOLOGIES_TABLE} { 
					id
					}
					... on ${DESIGN_COMMON_TYPES.SINGLE_ASSET} { 
					id
					}
					... on ${COMMON_CONTENT_TYPES.TEXT_MODULE} { 
					id
					}
					... on ${COMMON_CONTENT_TYPES.CHECK_OTHERS} {
					id
					}
				}
				elevatorText
			}
			content {
				__typename
				... on ${DESIGN_COMMON_TYPES.HEADER_AND_DESCRIPTION} {
					id
					header
					description
					paddings{
						top
						bottom
					}
					colorScheme{
						colorScheme
					}
				}
				... on ${DESIGN_COMMON_TYPES.SINGLE_ASSET} {
					id
					title
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
					externalVideoUrl {
						url
						width
						height
					}
					asset {
						url
						mimeType
						id
						width
						height
					}
					carouselText
					animationExpandOnScroll
					assetVariant
                    backgroundColor {
                      hex
                    }
                    fullScreenVideo{
					  link{
						url
					  }
					}
				}
				... on PaddingRecord {
					size
				}
				... on ${DESIGN_COMMON_TYPES.ASSETS_GALLERY} {
					id
					galleryName
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
					assets {
						url
						mimeType
						id
						width
						height
					}
				}
				... on ${DESIGN_COMMON_TYPES.ASSET_WITH_HEADER} {
					id
					header
					description
					asset {
						url
					}
					right
					paddings{
						top
						bottom
					}
					colorScheme{
						colorScheme
					}
				}
				... on ${DESIGN_COMMON_TYPES.PROJECT_SECTION_HEADER} {
					title
					text
					anchor
					id
					paddings{
						top
						bottom
					}
					colorScheme{
						colorScheme
					}
				}
				... on ${DESIGN_COMMON_TYPES.PROJECT_RANGE} {
					id
					title
					projectShortDescription
					projectDescription
					client {
						clientName
					}
					industry {
						industryName
					}
					competences {
					  competenceName
					  slug
					}
					serviceRange {
					__typename
					... on ${COMMON_CONTENT_TYPES.COMPETENCES_PAGE_RECORD} {
						id
						competenceName
						slug
						}
						... on ${COMMON_CONTENT_TYPES.SERVICE_PAGE_RECORD} {
						id
						serviceName
						slug
						}
					}
					technologies {
						projectTechnologyName
					}
					tools { 
						projectTechnologyName
						slug
					}
					year
					year2
					link {
						buttonText
						variant
						url
					}
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
				}
				... on ${COMMON_CONTENT_TYPES.PROJECT_SCOPE} {
					id
					title
					stages {
					  id
					  name
					  description
					  image {
						image {
						  id
						  url
						  blurUpThumb
						  focalPoint {
							x
							y
						  }
						  alt
						}
					  }
					}
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
				}
				... on ${COMMON_CONTENT_TYPES.TEXT_MODULE} {
					id
					chooseTextModuleVariants {
						__typename
						... on ${COMMON_CONTENT_TYPES.QUOTE_BLOCK} {
							quote
							author
							id
							paddings{
								top
								bottom
							}
							colorScheme{
								colorScheme
							}
						}
						... on ${COMMON_CONTENT_TYPES.BIG_HEADER_DESCRIPTION_TWO_COLUMNS_BLOCK} {
							header
							description
							id
							paddings{
								top
								bottom
							}
							colorScheme{
								colorScheme
							}
							hasBackgroundOnServicePage
						}
						... on ${COMMON_CONTENT_TYPES.TEXT_CENTERED_BLOCK} {
							text
							id
							paddings{
								top
								bottom
							}
							colorScheme{
								colorScheme
							}
						}
						... on ${COMMON_CONTENT_TYPES.HEADER_DESCRIPTION_TWO_COLUMNS_BLOCK} {
							header
							description
							id
							paddings{
								top
								bottom
							}
							colorScheme{
								colorScheme
							}
						}
						... on ${COMMON_CONTENT_TYPES.HEADER_DESCRIPTION_HALF_WIDTH_BLOCK} {
							header
							description
							id
							alignToRight
							paddings{
								top
								bottom
							}
							colorScheme{
								colorScheme
							}
						}
						... on ${COMMON_CONTENT_TYPES.TEXT_BREAK} {
							id
							text
							colorScheme{
								colorScheme
							}
							paddings {
							  top
							  bottom
							}
							breaks {
								... on ${COMMON_CONTENT_TYPES.IMAGE_BREAK} {
									image {
										url
									}
								}
							}
						}
						... on ${COMMON_CONTENT_TYPES.TYPOGRAPHY_BIG} {
							text
							paddings{
								top
								bottom
							}
							colorScheme {
								colorScheme
							}
						}
						... on ${COMMON_CONTENT_TYPES.TYPOGRAPHY_MEDIUM_TEXT} {
							id
							text
							align
							paddings {
								top
								bottom
							}
							colorScheme {
								colorScheme
							}
						}
					}
				}
				... on ${DESIGN_COMMON_TYPES.PINNED_TEXT_WITH_IMAGE} {
					id
					header
					description
					noPin
					imagetoedge
					image {
						url
						mimeType
						width
						height
					}
					videoUrl {
					  url
					  width
					  height
					}
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
					imageOnRightSide
				}
                ... on ${COMMON_CONTENT_TYPES.PROJECT_SLIDER} {
                	id
                    title
                    slides {
                        url
                    }
					paddings{
					  	top
					 	bottom
					}
					colorScheme{
						colorScheme
					}
                }
                ... on ${DESIGN_COMMON_TYPES.TECHNOLOGIES_TABLE} {
                	id
                    title
                    text
					paddings{
						top
						bottom
					}
					colorScheme{
						colorScheme
					}
        		}
			}
			nextProject {
				id
				tag
				projectLink {
					text
					variant
					link {
						... on ${DESIGN_COMMON_TYPES.SINGLE_PROJECT} {
							name
							slug
							description
							themeLight {
								mainPrimary {
									hex
								}
								mainSecondary{
									hex
								}
							}
							themeDark {
								mainPrimary {
									hex
								}
								mainSecondary{
									hex
								}
							}
						}
					}
				}
			}
		}
	}
`;

export default function Page({ data, globals, locale }) {
	const {
		name,
		description,
		projectColor,
		nextProject,
		bannerHeader,
		bannerShortenedRange,
		bannerImagesCollection,
		bannerHeader1,
		bannerHeader2,
		bannerHeaderColor,
		bannerDescription,
		contentCollection,
		year,
		projectServiceRange,
		projectNavigationElevator,
		content,
		themeLight,
	} = data;
	const projectElevatorText = globals?.projectElevatorText || '';
	const NextProjectRef = useRef()
	const extendedTheme = useExtendedTheme()
	const router = useRouter()
	const scroll = useScrollContext()
	const bannerData = {
		name,
		description
	};
	const bgColor = themeLight?.mainPrimary?.hex|| 'rgba(238, 239, 241, 1)'
	const textColor = themeLight?.mainSecondary?.hex || 'rgb(28,28,28)'
	const nextProjectProps = useMemo(() => {
		//get project colors based on current theme mode, if not found use fallbacks
		const mode = capitalize(extendedTheme.mode)
		const bgColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainPrimary?.hex|| 'lightGray'
		const textColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainSecondary?.hex || 'black'
		return {
			data: {
				tag: nextProject?.tag || '',
				name: nextProject?.projectLink?.link?.name || '',
				description: nextProject?.projectLink?.link?.description || '',
				button: {
					text: nextProject?.projectLink?.text || '',
					variant: nextProject?.projectLink?.variant || 'primary',
					slug: nextProject?.projectLink?.link?.slug || '',
				}
			},
			bgColor,
			textColor
		}
	},[nextProject, extendedTheme.mode])
	const onButtonClick = useCallback((slug) => {
		setCssVars(document.body, { 'project-color': nextProjectProps.bgColor })
		router.push(slug).then(() => {
			//reset project color on route change end
			setCssVars(document.body, { 'project-color': 'unset' })
		})
	},[scroll, nextProjectProps])
	const projectNavigationElevatorData = projectNavigationElevator && projectNavigationElevator.map((el) => {
		return {
			anchor: el?.link?.id || '',
			id: el?.link?.id || '',
			text: el?.elevatorText || '',
			afterPin: el?.afterPin || false,
		};
	})
	return (
		<>
			<StickyHeading stickyHeadingsSelectors={[]} />
			{projectNavigationElevatorData?.length > 0 && <SingleProjectElevatorNav data={projectNavigationElevatorData} locale={locale} projectElevatorText={projectElevatorText} />}
			<ProjectBanner data={bannerData} animate={false}  />
			<div style={{height: "1px"}}
				//TODO remove in future
			/>
			<ExtendedContent content={content} locale={locale} bgColor={bgColor} textColor={textColor} />
			<NextProject
				key={router.asPath}
				ref={NextProjectRef}
				onButtonClickStart={() => {
					scroll.instance.scrollTo(NextProjectRef.current, { duration, lock: true })
				}}
				onButtonClickComplete={onButtonClick}
				{...nextProjectProps}
			/>
		</>
	);
}

export async function getStaticProps({ params, locale }) {
	const { slug } = params;
	const data = await getPageData('allDesignSingleProjects', slug, propsQuery, locale);
	const { globals, cookies, footer, menu } = await getLayoutData(locale)
	return {
		props: {
			data,
			globals,
			cookies,
			locale,
			footer,
			menu
		},
	};
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const projectPosts = await getPages('allDesignSingleProjects', pathsQuery, locale);
		const posts = projectPosts.map((page) => ({
			params: {
				slug: page.slug,
			},
			locale,
		}));
		paths.push(...posts);
	}
	// { fallback: false } means other routes should 404.
	return {
		paths,
		fallback: false,
	};
}
